/**
 * AlertModal — replaces native window.alert() and window.confirm() with a
 * polished in-app modal. Supports four variants: info, success, error, warning.
 *
 * Usage (via hook):
 *   const { alert, confirm } = useAlert();
 *   await alert('Something went wrong', { variant: 'error' });
 *   const ok = await confirm('Delete this item?', { variant: 'warning' });
 */
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiAlertCircle, FiCheckCircle, FiInfo, FiAlertTriangle, FiX } from 'react-icons/fi';
import styles from './AlertModal.module.css';

const AlertContext = createContext(null);

const ICONS = {
  info:    <FiInfo />,
  success: <FiCheckCircle />,
  error:   <FiAlertCircle />,
  warning: <FiAlertTriangle />,
};

const COLORS = {
  info:    '#3b82f6',
  success: '#f0a818',
  error:   '#e50914',
  warning: '#f59e0b',
};

export function AlertProvider({ children }) {
  const [modal, setModal] = useState(null);
  const resolveRef = useRef(null);

  const open = useCallback((message, opts = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setModal({
        message,
        title: opts.title,
        variant: opts.variant || 'info',
        confirmLabel: opts.confirmLabel || (opts.isConfirm ? 'Confirm' : 'OK'),
        cancelLabel: opts.cancelLabel || 'Cancel',
        isConfirm: !!opts.isConfirm,
      });
    });
  }, []);

  const close = useCallback((value) => {
    resolveRef.current?.(value);
    resolveRef.current = null;
    setModal(null);
  }, []);

  const alert = useCallback(
    (message, opts) => open(message, { ...opts, isConfirm: false }),
    [open]
  );

  const confirm = useCallback(
    (message, opts) => open(message, { ...opts, isConfirm: true }),
    [open]
  );

  return (
    <AlertContext.Provider value={{ alert, confirm }}>
      {children}
      <AnimatePresence>
        {modal && (
          <motion.div
            className={styles.backdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => close(false)}
          >
            <motion.div
              className={styles.modal}
              initial={{ opacity: 0, scale: 0.88, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.88, y: 24 }}
              transition={{ type: 'spring', stiffness: 380, damping: 28 }}
              onClick={(e) => e.stopPropagation()}
              role="alertdialog"
              aria-modal="true"
            >
              <button
                className={styles.closeBtn}
                onClick={() => close(false)}
                aria-label="Close"
              >
                <FiX />
              </button>

              <div
                className={styles.iconWrap}
                style={{
                  color: COLORS[modal.variant],
                  background: `${COLORS[modal.variant]}1a`,
                }}
              >
                {ICONS[modal.variant]}
              </div>

              {modal.title && <h3 className={styles.title}>{modal.title}</h3>}
              <p className={styles.message}>{modal.message}</p>

              <div className={styles.actions}>
                {modal.isConfirm && (
                  <button
                    className={styles.cancelBtn}
                    onClick={() => close(false)}
                  >
                    {modal.cancelLabel}
                  </button>
                )}
                <button
                  className={styles.confirmBtn}
                  style={{ background: COLORS[modal.variant] }}
                  onClick={() => close(true)}
                  autoFocus
                >
                  {modal.confirmLabel}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AlertContext.Provider>
  );
}

export function useAlert() {
  const ctx = useContext(AlertContext);
  if (!ctx) throw new Error('useAlert must be inside <AlertProvider>');
  return ctx;
}
